import axios from 'axios';
import { API_BASE_URL } from '../config/backendConfig';
import { refreshSession } from './authApi';

const ACCESS_KEY = 'yovo_auth_token';
const REFRESH_KEY = 'yovo_refresh_token';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 80000, // 80 seconds (increased for flight search which can take longer)
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let refreshPromise = null;

async function tryRefreshAccessToken() {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return null;
  if (!refreshPromise) {
    refreshPromise = refreshSession(refreshToken)
      .then((data) => {
        const access = data.accessToken || data.token;
        if (!access) return null;
        localStorage.setItem(ACCESS_KEY, access);
        if (data.refreshToken) {
          localStorage.setItem(REFRESH_KEY, data.refreshToken);
        }
        return access;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function hardClearAuthAndRedirect() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  const path = window.location.pathname;
  const bookingPaths = [
    '/booking-confirmation',
    '/view-booking-details',
    '/flight-booking-page',
    '/payment',
    '/e-ticket',
    '/hotel-booking-confirmation'
  ];
  const isBookingPage = bookingPaths.some(p => path.startsWith(p));
  if (!isBookingPage) {
    window.location.href = '/';
  }
}

// Response interceptor — try refresh once on ACCESS_EXPIRED, else clear session
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const code = error.response?.data?.code;

    if (
      status === 401 &&
      original &&
      !original._retry &&
      !String(original.url || '').includes('/auth/refresh') &&
      !String(original.url || '').includes('/auth/logout')
    ) {
      // Prefer explicit ACCESS_EXPIRED; also try refresh for generic 401 if refresh exists
      const hasRefresh = !!localStorage.getItem(REFRESH_KEY);
      if (code === 'ACCESS_EXPIRED' || (hasRefresh && code !== 'ACCESS_INVALID')) {
        original._retry = true;
        const newAccess = await tryRefreshAccessToken();
        if (newAccess) {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newAccess}`;
          return api(original);
        }
      }
      hardClearAuthAndRedirect();
    }
    return Promise.reject(error);
  }
);

// API service functions
export const apiService = {
  // Health check
  health: () => api.get('/health'),

  // Users
  getUsers: () => api.get('/users'),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (userData) => api.post('/users', userData),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/users/${id}`),

  // Trips
  getTrips: () => api.get('/trips'),
  getTrip: (id) => api.get(`/trips/${id}`),
  createTrip: (tripData) => api.post('/trips', tripData),
  updateTrip: (id, tripData) => api.put(`/trips/${id}`, tripData),
  deleteTrip: (id) => api.delete(`/trips/${id}`),

  // Trains
  searchStations: (query) => api.get(`/trains/stations/search?q=${encodeURIComponent(query)}`),
  searchTrains: (from, to, date, quota = 'GN', fromName = null, fromCity = null, toName = null, toCity = null) => {
    let url = `/trains/search?from=${from}&to=${to}&date=${date}&quota=${quota}`;
    if (fromName) url += `&fromName=${encodeURIComponent(fromName)}`;
    if (fromCity) url += `&fromCity=${encodeURIComponent(fromCity)}`;
    if (toName) url += `&toName=${encodeURIComponent(toName)}`;
    if (toCity) url += `&toCity=${encodeURIComponent(toCity)}`;
    return api.get(url);
  },
  getCoachDetails: (trainNumber, classCode, from, to, date, quota = 'GN') => 
    api.get(`/trains/${trainNumber}/coach/${classCode}?from=${from}&to=${to}&date=${date}&quota=${quota}`),
  getTrainSchedule: (trainNumber) => api.get(`/trains/${trainNumber}/schedule`),
  
  // PNR Status - RapidAPI IRCTC
  getPNRStatus: (pnrNumber) => {
    // This will call the backend API which will then call RapidAPI IRCTC
    return api.get(`/trains/pnr/${pnrNumber}`);
  },

  // Flights (TBO Backend)
  searchFlights: (searchParams) => {
    console.log('📡 API Service - Calling /flights/search with:', searchParams);
    return api.post('/flights/search', searchParams).catch(error => {
      console.error('📡 API Service - Request failed:', {
        url: '/flights/search',
        method: 'POST',
        params: searchParams,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    });
  },
  getFareQuote: (sessionId, resultIndex, ibResultIndex = null) => {
    const payload = { sessionId, resultIndex };
    if (ibResultIndex) {
      payload.ibResultIndex = ibResultIndex;
    }
    return api.post('/flights/fare-quote', payload);
  },
  getFareUpsell: (sessionId, resultIndex) => {
    return api.post('/flights/fare-upsell', { sessionId, resultIndex });
  },
  getCalendarFare: (origin, destination, month) => {
    return api.get('/flights/calendar-fare', { params: { origin, destination, month } });
  },
  getConvenienceFeeConfig: (params) => {
    return api.get('/flights/convenience-fee-config', { params });
  },
  getFareRule: (sessionId, resultIndex, ibResultIndex = null) => {
    const payload = { sessionId, resultIndex };
    if (ibResultIndex) {
      payload.ibResultIndex = ibResultIndex;
    }
    return api.post('/flights/fare-rule', payload);
  },
  selectFareQuote: (sessionId, selectedFareQuote, selectedResultIndex, selectedPrice, selectedFarePlanName, journeyType, returnType) => {
    return api.post('/flights/fare-quote/select', {
      sessionId,
      selectedFareQuote,
      selectedResultIndex,
      selectedPrice,
      selectedFarePlanName,
      journeyType,
      returnType
    }, { timeout: 155000 });
  },
  revalidateFareQuote: (sessionId) => {
    return api.post('/flights/fare-quote/revalidate', { sessionId });
  },
  
  // Passenger Details
  savePassengerDetails: (sessionId, passengerData) => {
    return api.post('/flights/passenger-details', {
      sessionId,
      ...passengerData
    });
  },
  
  // Saved Profiles (requires auth)
  getSavedProfiles: () => {
    return api.get('/flights/passenger-profiles');
  },
  
  getSavedBilling: () => {
    return api.get('/flights/billing-details');
  },
  
  // Check for duplicate booking
  checkDuplicateBooking: (sessionId, passengers) => {
    return api.post('/flights/booking/check-duplicate', { sessionId, passengers });
  },

  // Get passenger details from session
  getPassengerDetailsFromSession: (sessionId) => {
    return api.get(`/flights/passenger-details/${sessionId}`);
  },
  
  // Get complete session data
  getSession: (sessionId) => {
    return api.get(`/flights/sessions/${sessionId}`);
  },
  
  // SSR (Special Service Request)
  getSSROptions: (sessionId, resultIndex, obResultIndex = null, ibResultIndex = null) => {
    const payload = { sessionId };
    if (obResultIndex && ibResultIndex) {
      // Normal Return: both OB and IB
      payload.obResultIndex = obResultIndex;
      payload.ibResultIndex = ibResultIndex;
    } else {
      // One-way or Special Return: single resultIndex
      payload.resultIndex = resultIndex;
    }
    return api.post('/flights/ssr', payload);
  },
  
  saveSSRSelections: (sessionId, selections) => {
    return api.post('/flights/ssr/select', {
      sessionId,
      selections
    });
  },
  
  // Save booking JSONs to session
  saveBookingJSONs: (sessionId, data) => {
    return api.post('/flights/booking-json', {
      sessionId,
      ...data
    });
  },
  
  // Payment APIs
  getPaymentQuote: (sessionId) => {
    return api.post('/flights/payment/quote', { sessionId });
  },

  initiatePayment: (sessionId, amount, currency = 'INR') => {
    return api.post('/flights/payment/initiate', {
      sessionId,
      amount,
      currency
    });
  },
  
  verifyPayment: (orderId, paymentId, signature) => {
    return api.post('/flights/payment/verify', {
      orderId,
      paymentId,
      signature
    });
  },
  
  // Booking Status
  getBookingStatus: (orderId, { sid } = {}) => {
    const params = new URLSearchParams({ orderId });
    if (sid) params.set('sid', sid);
    return api.get(`/flights/booking/status?${params.toString()}`);
  },

  // Get full booking details from TBO
  getBookingDetails: (pnr, firstName, lastName, orderId = null, sid = null) => {
    const body = { pnr, firstName, lastName, orderId };
    if (sid) body.sid = sid;
    return api.post('/flights/booking/details', body);
  },

  getBookingSummaryBySession: (sessionId) => api.get(`/flights/booking/session/${sessionId}`),

  // Get logged-in user's flight trips (requires auth)
  getMyTrips: () => api.get('/flights/my-trips'),

  // Cancellation APIs
  getBookingDataForCancellation: (bookingDbId) => {
    return api.get(`/flights/cancellation/booking-data/${bookingDbId}`);
  },

  // P0-3: Get cancellation charges quote before confirming
  // P0-Scope: Accepts optional scope parameters
  getCancellationQuote: (bookingDbId, scopeOptions = {}) => {
    return api.post('/flights/cancellation/quote', { 
      bookingDbId,
      scopeType: scopeOptions.scopeType,
      selectedPassengerIndices: scopeOptions.selectedPassengerIndices,
      selectedSegmentIndices: scopeOptions.selectedSegmentIndices,
    });
  },

  // P0-3: Updated to require quoteId and acknowledgement
  initiateCancellation: ({ bookingDbId, quoteId, selectedPassengerIndices, cancelReason, cancelDescription, acknowledgeEstimate }) => {
    return api.post('/flights/cancellation/initiate', {
      bookingDbId,
      quoteId,
      selectedPassengerIndices,
      cancelReason,
      cancelDescription,
      acknowledgeEstimate,
    });
  },

  getCancellationStatus: (bookingDbId) => {
    return api.get(`/flights/cancellation/status/${bookingDbId}`);
  },

  getCancellationById: (cancellationId) => {
    return api.get(`/flights/cancellation/${cancellationId}`);
  },

  // P0-7: Service Request APIs
  createServiceRequest: ({ bookingId, requestType, requestSubtype, customerReason, customerDescription, customerAcknowledged }) => {
    return api.post('/flights/service-requests', {
      bookingId,
      requestType,
      requestSubtype,
      customerReason,
      customerDescription,
      customerAcknowledged,
    });
  },

  getServiceRequestByReference: (reference) => {
    return api.get(`/flights/service-requests/reference/${reference}`);
  },

  getServiceRequestsByBooking: (bookingId) => {
    return api.get(`/flights/service-requests/booking/${bookingId}`);
  },

  // P0-7B: Get all service requests for the current user (My Trips Support tab)
  getMyServiceRequests: () => {
    return api.get('/flights/service-requests/my-requests');
  },

  // P0-7C: Accept assisted cancellation quote
  acceptServiceRequestQuote: (reference) => {
    return api.post(`/flights/service-requests/${reference}/accept-quote`);
  },

  // P0-7C: Decline assisted cancellation quote
  declineServiceRequestQuote: (reference) => {
    return api.post(`/flights/service-requests/${reference}/decline-quote`);
  },

  getDestinationInfo: (queryString) => {
    return api.get(`/destination/info?${queryString}`);
  },

  getRouteRating: (origin, destination) => {
    return api.get(`/flightstats/route/${origin}/${destination}`);
  },

  getWeatherByAirport: (airportCode) => {
    return api.get(`/destination/info?airportCode=${airportCode}`);
  },

  submitBookingFeedback: (data) => api.post('/feedback/booking', data),
  checkBookingFeedback: (orderId) => api.get(`/feedback/booking/${orderId}`),

  submitHotelBookingFeedback: (data) => api.post('/feedback/hotel-booking', data),
  checkHotelBookingFeedback: (orderId) => api.get(`/feedback/hotel-booking/${orderId}`),

  // ── Flight Assistant (Help Me Choose) ─────────────────────────────
  parseFlightIntent: (query, context) => {
    return api.post('/flight-assistant/parse-intent', { query, context }, { timeout: 12000 });
  },

  // ── Hotel Session ───────────────────────────────────────────────
  getHotelPreview: (sessionId) => {
    return api.get(`/hotels/tbo/session/${sessionId}/preview`);
  },

  saveHotelGuests: (sessionId, guestDetails, contactDetails, corporatePan, billingDetails) => {
    const body = { guestDetails, contactDetails };
    if (corporatePan) body.corporatePan = corporatePan;
    if (billingDetails) body.billingDetails = billingDetails;
    return api.put(`/hotels/tbo/session/${sessionId}/guests`, body);
  },

  // ── Hotel PreBook Recheck ───────────────────────────────────────
  recheckHotelPrebook: (sessionId) => {
    return api.post('/hotels/tbo/prebook/recheck', { sessionId });
  },

  confirmHotelRecheck: (sessionId, latestRoom) => {
    return api.post('/hotels/tbo/prebook/recheck/confirm', { sessionId, latestRoom });
  },

  // ── Hotel Payment APIs ─────────────────────────────────────────
  getHotelPaymentQuote: (sessionId) => {
    return api.post('/hotels/tbo/payment/quote', { sessionId });
  },

  initiateHotelPayment: (sessionId) => {
    return api.post('/hotels/tbo/payment/initiate', { sessionId });
  },

  verifyHotelPayment: (orderId, paymentId, signature) => {
    return api.post('/hotels/tbo/payment/verify', { orderId, paymentId, signature });
  },

  getHotelBookingStatus: (orderId) => {
    return api.get(`/hotels/tbo/booking/status/${encodeURIComponent(orderId)}`);
  },

  getHotelBookingDetails: (bookingId, { sid } = {}) => {
    const params = new URLSearchParams();
    if (sid) params.set('sid', sid);
    const qs = params.toString();
    return api.get(`/hotels/tbo/booking/${bookingId}${qs ? `?${qs}` : ''}`);
  },

  getMyHotelBookings: () => {
    return api.get('/hotels/tbo/my-bookings');
  },

  getHotelCancelQuote: (bookingId) => {
    return api.get(`/hotels/tbo/booking/${bookingId}/cancel-quote`);
  },

  initiateHotelCancellation: (bookingId, reason, idempotencyKey) => {
    return api.post(`/hotels/tbo/booking/${bookingId}/cancel`, { reason, idempotencyKey });
  },

  getHotelCancellationStatus: (bookingId) => {
    return api.get(`/hotels/tbo/booking/${bookingId}/cancel-status`);
  },

  // ── Hotel Invoice API ─────────────────────────────────────────
  getHotelInvoice: (bookingId) => {
    return api.get(`/hotels/tbo/booking/${bookingId}/invoice`);
  },

  // ── Hotel Credit Note API ────────────────────────────────────────
  getHotelCreditNote: (cancellationId) => {
    return api.get(`/hotels/tbo/cancellations/${cancellationId}/credit-note`);
  },

  // ── Flight E-Ticket PDF Download ────────────────────────────────
  downloadETicketPdf: (bookingDbId, sid = null) => {
    const params = sid ? `?sid=${encodeURIComponent(sid)}` : '';
    return api.get(`/flights/booking/${bookingDbId}/eticket${params}`, { responseType: 'blob' });
  },

  // ── Flight Invoice API ─────────────────────────────────────────
  getFlightInvoice: (bookingId) => {
    return api.get(`/flights/bookings/${bookingId}/invoice`);
  },

  // ── Flight Credit Note API ─────────────────────────────────────
  getFlightCreditNote: (cancellationId) => {
    return api.get(`/flights/cancellations/${cancellationId}/credit-note`);
  },

  // ── Flight Reschedule API (P0 Reschedule) ────────────────────────
  searchRescheduleOptions: (bookingDbId, preferredDate, timeWindow = 'any') => {
    return api.post('/flights/reschedule/search', { bookingDbId, preferredDate, timeWindow });
  },

  createRescheduleRequest: (data) => {
    return api.post('/flights/reschedule/request', data);
  },

  acceptRescheduleQuote: (reference) => {
    return api.post(`/flights/reschedule/${reference}/accept-quote`);
  },

  declineRescheduleQuote: (reference) => {
    return api.post(`/flights/reschedule/${reference}/decline-quote`);
  },

  verifyReschedulePayment: (reference, paymentData) => {
    return api.post(`/flights/reschedule/${reference}/verify-payment`, paymentData);
  },

  getReschedulePaymentStatus: (reference) => {
    return api.get(`/flights/reschedule/${reference}/payment-status`);
  },

  getRescheduleEstimate: (scopeType, passengerCount, segmentCount) => {
    return api.get(`/flights/reschedule/estimate?scopeType=${scopeType}&passengerCount=${passengerCount}&segmentCount=${segmentCount}`);
  },

  getLinkedSegments: (bookingId) => {
    return api.get(`/flights/reschedule/booking/${bookingId}/linked-segments`);
  },

  // ── Insurance APIs ──────────────────────────────────────────────
  getInsuranceQuoteForSession: (sessionId) => {
    return api.get(`/insurance/quote-for-session/${sessionId}`);
  },

  selectInsurance: (data) => {
    return api.post('/insurance/select', data);
  },

  // ── CFAR APIs ─────────────────────────────────────────────────
  getCfarQuoteForSession: (sessionId) => {
    return api.get(`/insurance/cfar/quote-for-session/${sessionId}`);
  },

  selectCfar: (data) => {
    return api.post('/insurance/cfar/select', data);
  },

  // ── Generic API methods ─────────────────────────────────────────
  get: (url, config) => api.get(url, config),
  post: (url, data, config) => api.post(url, data, config),
  put: (url, data, config) => api.put(url, data, config),
  delete: (url, config) => api.delete(url, config),
};

export default api; 